import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuFileClock } from "react-icons/lu";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";

import { Formik, Form, Field } from "formik";
import FormikInput from "@components/form/FormikInput";
import FormikDatepicker from "@components/form/FormikDatepicker";

import { savePto } from "@service/ptoService";

import HeadlessDialog from "@components/Dialog";
import savePtoSchema from "@validators/savePtoSchema";
import Button from "@components/Button";
import SectionHeading from "@components/SectionHeading";
// import WeekSnapper from "@components/form/WeekSnapper";
import { getNextSaturday, getPreviousSunday } from "@utils/general";
import Spinner from "@components/Spinner";

interface IPtoEditProps {
  contractor_id: number;
  contractor_name: string;
  isOpen: boolean;
  onClose: () => void;
}

interface iFormValues {
  start_date: Date;
  end_date: Date;
  hours_requested: string;
}

let start_date = getPreviousSunday(null, -1);
const end_date = start_date ? getNextSaturday(null, -1) : null;

export function PtoEdit({
  contractor_id,
  contractor_name,
  isOpen,
  onClose,
}: IPtoEditProps): JSX.Element {
  const { t } = useTranslation();
  const [sendingSubmit, setSendingSubmit] = useState<boolean>(false);
  const [alertModal, setAlertModal] = useState<{ open: boolean; title: string; message: string }>({
    open: false,
    title: "",
    message: "",
  });

  function showAlert(title: string, message: string) {
    setAlertModal({ open: true, title, message });
  }

  const initialValues: iFormValues = {
    start_date: start_date ?? new Date(),
    end_date: end_date ?? new Date(),
    hours_requested: "",
  };

  const validationSchema = savePtoSchema(t);

  const handleSubmit = useCallback(
    async (
      values: iFormValues,
      { setSubmitting }: import("formik").FormikHelpers<iFormValues>
    ) => {
      try {
        setSendingSubmit(true);
        const data = {
          contractor_id: contractor_id,
          start_date: values.start_date,
          end_date: values.end_date,
          hours_requested: values.hours_requested,
        };
        await savePto(data);
        showAlert("Success", "Edit PTO executed successfully.");
        onClose();
      } catch (err) {
        // console.log(err);
        let errorMessage = "Unknown error";
        if (err && typeof err === "object") {
          if ("response" in err && typeof (err as any).response === "object") {
            errorMessage =
              (err as any).response?.data?.error ||
              (err as any).message ||
              errorMessage;
          } else if ("message" in err) {
            errorMessage = (err as any).message || errorMessage;
          }
        }
        showAlert("Error", `Edit PTO failed: ${errorMessage}`);
      } finally {
        setSubmitting(false);
        setSendingSubmit(false);
        // alert(t("Pto.success"));
      }
    },
    [contractor_id, onClose]
  );

  return (
    <>
      {isOpen && (
        <HeadlessDialog
          title={
            <div className="tw-flex tw-items-center tw-gap-2">
              <LuFileClock className="tw-text-primary-light" size={24} />
              <span>{t("Pto.title")}</span>
            </div>
          }
          description=""
          isOpen={isOpen}
          setIsOpen={onClose}
          onClose={onClose}
          buttonText="Close"
          size="tw-w-[100vw] md:tw-max-w-[40vw]"
        >
          <Formik<iFormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <div className="tw-px-6 tw-pb-6" data-testid="form">
                {/* <WeekSnapper /> */}
                <Form>
                  <SectionHeading className="tw-mb-6">
                    {contractor_name}
                  </SectionHeading>
                  <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div>
                      <Field
                        name="start_date"
                        label={t("Pto.start_date")}
                        as={FormikDatepicker}
                        error={touched.start_date && errors.start_date}
                      />
                    </div>
                    <div>
                      <Field
                        name="end_date"
                        label={t("Pto.end_date")}
                        as={FormikDatepicker}
                        error={touched.end_date && errors.end_date}
                      />
                    </div>
                    <div>
                      <Field
                        name="hours_requested"
                        label={t("Pto.hours_requested")}
                        as={FormikInput}
                        inputMode="numeric"
                        error={
                          touched.hours_requested && errors.hours_requested
                        }
                      />
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-end tw-mt-4">
                    <Button
                      type="submit"
                      disabled={sendingSubmit}
                      className="md:tw-w-[22%]"
                      // className="tw-bg-blue-500 tw-text-white tw-px-4 tw-py-2 tw-rounded hover:tw-bg-blue-600"
                    >
                      {sendingSubmit ? (
                        <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" />
                      ) : (
                        "Submit PTO"
                      )}
                    </Button>
                  </div>
                </Form>
              </div>
            )}
          </Formik>
        </HeadlessDialog>
      )}

      <HeadlessDialog
        title={alertModal.title}
        description={alertModal.message}
        isOpen={alertModal.open}
        setIsOpen={(open: boolean) => {
          if (!open) setAlertModal((prev) => ({ ...prev, open: false }));
        }}
        buttonText="OK"
        icon={
          alertModal.title === "Success" ? (
            <FiCheckCircle className="tw-text-green-500" size={24} />
          ) : (
            <FiAlertCircle className="tw-text-red-500" size={24} />
          )
        }
      >
        {null}
      </HeadlessDialog>
    </>
  );
}
